import type { AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { Autocomplete } from '@openx/design-system/components/Autocomplete/Autocomplete.tsx';
import { CaretDownIcon } from '@phosphor-icons/react/CaretDown';
import { XIcon } from '@phosphor-icons/react/X';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { DebounceField } from '../DebounceField/DebounceField.tsx';
import { TextField } from '../TextField/TextField.tsx';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled.ts';
import { AutoCompleteRenderOptions } from './AutoCompleteRenderOptions.tsx';
import {
  AutocompleteDesignSystemScope,
  designSystemChipProps,
  designSystemDefaultProps,
  renderDesignSystemInput,
} from './autocompleteDesignSystem.tsx';
import type { RenderOptions, RHFAutocompleteProps } from './types.ts';

const chipProps: ChipProps = {
  // @ts-expect-error
  // there is no type definition for data-* attributes for ChipProps, although it works and we keep it because it's used for testing
  'data-test': 'chip-button',
  deleteIcon: (
    <IconButton size="large">
      <XIcon data-test="chip-button-delete-icon" />
    </IconButton>
  ),
};

const defaultProps = {
  disabledItemsFocusable: true,
  limitTags: 1,
  multiple: undefined,
  popupIcon: <CaretDownIcon />,
};

const dsChipProps = designSystemChipProps('chip-button-delete-icon');

type RenderOption<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Exclude<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderOption'], undefined>;

export const RHFAutoComplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  textFieldProps,
  loading,
  debounced = false,
  onDebounceChange,
  onOptionBlur,
  renderOptions,
  onInputChange,
  isOptionEqualToValue,
  value,
  onChange,
  slotProps,
  designSystem,
  ...rest
}: RHFAutocompleteProps<T, Multiple, DisableClearable, FreeSolo, TFieldValues>): JSX.Element => {
  const isDesignSystem = useDesignSystemEnabled('autocomplete', designSystem);
  const {
    field: { onChange: fieldOnChange, onBlur: fieldOnBlur, ...restFieldProps },
    fieldState,
    formState: { isSubmitting },
  } = useController({
    control,
    name,
  });

  const [inputValue, setInputValue] = useState('');

  const TextInputComponent = useMemo(() => (debounced ? DebounceField : TextField), [debounced]);

  const handleInputChange = useCallback(
    (e, newValue: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'reset') {
        setInputValue('');
      } else {
        setInputValue(newValue);
      }
      onInputChange?.(e, newValue, reason);
    },
    [onInputChange]
  );

  const getOptionValue = useCallback(
    (
      option: T,
      getOptionValue?: RenderOptions<T>['getOptionValue'],
      getOptionLabel?: RenderOptions<T>['getOptionValue']
    ) => {
      if (getOptionValue) return getOptionValue(option);
      if (getOptionLabel) return getOptionLabel(option);

      return typeof option === 'string' ? option : '';
    },
    []
  );

  const getDataTest = useCallback((option: T, dataTest?: string | ((option: T) => string)) => {
    return typeof dataTest === 'string' ? dataTest : dataTest?.(option);
  }, []);

  const hasMultiple = useMemo(() => rest.multiple === true, [rest.multiple]);

  const onOptionChange = useCallback(
    (_, value, reason, details) => {
      onChange ? onChange(_, value, reason, details) : fieldOnChange(value);
    },
    [fieldOnChange, onChange]
  );

  const onBlur = useCallback(() => {
    fieldOnBlur();
    onOptionBlur?.();
  }, [fieldOnBlur, onOptionBlur]);

  const renderOption = useCallback<RenderOption<T, Multiple, DisableClearable, FreeSolo>>(
    (props, option: T, { selected }) => {
      return (
        <li {...props} key={props.key}>
          <AutoCompleteRenderOptions
            option={getOptionValue(option, renderOptions?.getOptionValue, rest?.getOptionLabel)}
            childrenLocation={renderOptions?.childrenLocation}
            dataTest={getDataTest(option, renderOptions?.dataTest)}
            phraseToHighlight={inputValue}
            getPaddingLevel={() => renderOptions?.getPaddingLevel?.(option)}
            tooltip={renderOptions?.tooltip}
            tooltipPlacement={renderOptions?.tooltipPlacement}
            tooltipMessage={renderOptions?.tooltipMessage?.(option)}
            hasMultiple={hasMultiple}
            selected={selected}
          >
            {renderOptions?.renderChildren?.(option)}
          </AutoCompleteRenderOptions>
        </li>
      );
    },
    [getDataTest, getOptionValue, hasMultiple, inputValue, renderOptions, rest?.getOptionLabel]
  );

  const renderLegacyInput = useCallback(
    ({ slotProps: paramsSlotProps, ...restParams }) => (
      <TextInputComponent
        designSystem={false}
        onChange={e => onDebounceChange?.(e)}
        {...restParams}
        {...textFieldProps}
        key={restParams.key}
        slotProps={{
          input: {
            disableUnderline: true,
            ...paramsSlotProps.input,
            endAdornment: (
              <>
                {loading && (
                  <Box
                    sx={{
                      pr: 1,
                    }}
                  >
                    <CircularProgress data-test="autocomplete-loading-indicator" size={20} />
                  </Box>
                )}
                {paramsSlotProps.input.endAdornment}
              </>
            ),
            readOnly: rest.textFieldReadOnly,
          },
          inputLabel: { disableAnimation: true, focused: false, shrink: true, ...paramsSlotProps.inputLabel },
          htmlInput: { ...paramsSlotProps.htmlInput },
        }}
        helperText={
          (Array.isArray(fieldState.error) && fieldState.error?.find(value => value !== undefined)?.message) ||
          fieldState.error?.message ||
          textFieldProps?.helperText
        }
        error={fieldState.invalid || textFieldProps?.error}
      />
    ),
    [
      TextInputComponent,
      fieldState.error,
      fieldState.invalid,
      loading,
      onDebounceChange,
      rest.textFieldReadOnly,
      textFieldProps,
    ]
  );

  const renderInput = useCallback(
    params =>
      isDesignSystem
        ? renderDesignSystemInput({
            params,
            // The legacy input ignores `textFieldProps.slotProps`; keep it that way so enabling the DS changes no call site.
            textFieldProps: textFieldProps && { ...textFieldProps, slotProps: undefined },
            debounced,
            onDebounceChange,
            loading,
            loadingDataTest: 'autocomplete-loading-indicator',
            textFieldReadOnly: rest.textFieldReadOnly,
            helperText:
              (Array.isArray(fieldState.error) && fieldState.error?.find(value => value !== undefined)?.message) ||
              fieldState.error?.message ||
              textFieldProps?.helperText,
            error: fieldState.invalid || textFieldProps?.error,
          })
        : renderLegacyInput(params),
    [
      debounced,
      fieldState.error,
      fieldState.invalid,
      isDesignSystem,
      loading,
      onDebounceChange,
      renderLegacyInput,
      rest.textFieldReadOnly,
      textFieldProps,
    ]
  );

  const autocomplete = (
    <StyledAutocomplete
      {...restFieldProps}
      {...(isDesignSystem ? designSystemDefaultProps : defaultProps)}
      slotProps={{
        chip: isDesignSystem ? dsChipProps : chipProps,
        ...slotProps,
      }}
      value={typeof value !== 'undefined' ? value : restFieldProps.value}
      onChange={onOptionChange}
      onBlur={onBlur}
      disableCloseOnSelect={hasMultiple}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      onInputChange={handleInputChange}
      {...rest}
      renderInput={renderInput}
      disabled={isSubmitting || rest.disabled}
    />
  );

  return isDesignSystem ? <AutocompleteDesignSystemScope>{autocomplete}</AutocompleteDesignSystemScope> : autocomplete;
};

const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop => typeof prop === 'string' && !['textFieldReadOnly', 'showSelectAllOptions'].includes(prop),
})<{
  textFieldReadOnly?: boolean;
}>`
  & .MuiAutocomplete-input {
    cursor: ${({ textFieldReadOnly }) => (textFieldReadOnly ? 'pointer' : 'auto')};
  }
` as typeof Autocomplete;
